import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, CheckCircle, Lightbulb, Shield, Leaf, Scale, Handshake, Rocket } from 'lucide-react';
import { useLanguage } from '../contexts/languageStore';
import { useWebsite } from '../contexts/websiteStore';

export function AboutUs() {
    const { t, isRTL } = useLanguage();
    const { config, getAsset } = useWebsite();
    const ref = useRef<HTMLDivElement>(null);
    const isInView = useInView(ref, { once: true, margin: "-50px" });


    return (
        <section id="about" className="relative overflow-hidden pt-24">
            {/* Interactive gradient background */}
            <motion.div
                className="absolute inset-0"
                animate={{
                    background: [
                        `radial-gradient(circle at 50% 50%, var(--primary-color-rgb, 59, 130, 246) / 0.12 0%, transparent 45%)`,
                        `radial-gradient(circle at 50% 50%, var(--secondary-color-rgb, 34, 197, 94) / 0.12 0%, transparent 45%)`,
                        `radial-gradient(circle at 50% 50%, var(--primary-color-rgb, 59, 130, 246) / 0.12 0%, transparent 45%)`
                    ]
                }}
                transition={{
                    duration: 1,
                    ease: "easeOut"
                }}
            />

            {/* Enhanced background with vibrant gradients */}
            <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-primary/3 to-secondary/3"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-primary/2 via-transparent to-secondary/2"></div>

            {/* Animated background elements */}
            <motion.div
                className="absolute top-0 left-0 w-full h-full"
                animate={{
                    background: [
                        `radial-gradient(circle at 80% 20%, var(--secondary-color-rgb, 34, 197, 94) / 0.08 0%, transparent 50%)`,
                        `radial-gradient(circle at 20% 80%, var(--primary-color-rgb, 59, 130, 246) / 0.08 0%, transparent 50%)`,
                        `radial-gradient(circle at 80% 20%, var(--secondary-color-rgb, 34, 197, 94) / 0.08 0%, transparent 50%)`
                    ]
                }}
                transition={{
                    duration: 10,
                    repeat: Infinity,
                    ease: "easeInOut"
                }}
            />

            <div className="container mx-auto px-6 relative z-10 max-w-6xl">

                {/* Header Section */}
                <motion.div
                    ref={ref}
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                    className="text-center mb-20"
                >

                    <motion.div
                        className="inline-flex items-center px-8 py-4 bg-primary/5 text-primary border border-primary/10 text-sm font-light tracking-wider mb-8"
                        initial={{ opacity: 0, y: 20 }}
                        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                        transition={{ delay: 0.2, duration: 0.8 }}
                    >
                        <span className="hidden sm:block w-px h-4 bg-gray-800 mr-4"></span>
                        <span className="uppercase tracking-[0.2em]">{t("about.title")}</span>
                        <span className="hidden sm:block w-px h-4 bg-gray-800 ml-4"></span>
                    </motion.div>

                    <motion.h2
                        className="text-5xl md:text-6xl font-light text-gray-900 mb-8 leading-tight"
                        initial={{ opacity: 0, y: 30 }}
                        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                        transition={{ delay: 0.3, duration: 1.2, ease: "easeOut" }}
                    >
                        {t("about.company.title")}
                    </motion.h2>

                    <motion.p
                        className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed tracking-wide"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5, duration: 1.2, ease: "easeOut" }}
                    >
                        {t("about.company.description")}
                    </motion.p>
                </motion.div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
                    {/* Text Content */}
                    <motion.div
                        initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
                        animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: isRTL ? 30 : -30 }}
                        transition={{ delay: 0.6, duration: 1.2, ease: "easeOut" }}
                    >
                        <h3 className="text-3xl font-light text-gray-900 mb-6">
                            {t("about.company.mission.title")}
                        </h3>
                        <p className="text-lg text-gray-600 leading-relaxed mb-8">
                            {t("about.company.mission.description")}
                        </p>

                        <div className="space-y-4 mb-8">
                            {[
                                t("about.company.expertise.primary"),
                                t("about.company.expertise.partnerships"),
                                t("about.company.expertise.logistics")
                            ].map((item, index) => (
                                <motion.div
                                    key={index}
                                    className="flex items-center group cursor-pointer"
                                    initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                                    animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: isRTL ? 20 : -20 }}
                                    transition={{ delay: 0.8 + index * 0.1, duration: 0.6 }}
                                    whileHover={{ x: isRTL ? -5 : 5 }}
                                >
                                    <motion.div
                                        whileHover={{ scale: 1.2, rotate: 360 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <CheckCircle className={`h-5 w-5 text-primary ${isRTL ? 'ml-3' : 'mr-3'} flex-shrink-0 group-hover:text-primary transition-colors duration-300`} />
                                    </motion.div>
                                    <span className="text-gray-900 font-light group-hover:text-primary transition-colors duration-300">{item}</span>
                                </motion.div>
                            ))}
                        </div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                            transition={{ delay: 1.2, duration: 0.8 }}
                        >
                            <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.95 }}>
                                <button className="bg-primary hover:bg-primary/90 text-white px-8 py-3 text-sm font-light tracking-wide transition-all duration-300 group rounded inline-flex items-center">
                                    <span className="hidden sm:inline-flex items-center">
                                        {t("about.company.learn_more")}
                                        <ArrowRight className={`${isRTL ? 'mr-2 rotate-180' : 'ml-2'} h-4 w-4 group-hover:translate-x-1 transition-transform duration-300`} />
                                    </span>
                                    <span className="sm:hidden inline-flex items-center">
                                        {t("about.company.learn_more_mobile")}
                                        <ArrowRight className={`${isRTL ? 'mr-2 rotate-180' : 'ml-2'} h-4 w-4 group-hover:translate-x-1 transition-transform duration-300`} />
                                    </span>
                                </button>
                            </motion.div>
                        </motion.div>
                    </motion.div>

                    {/* Main Image */}
                    <motion.div
                        initial={{ opacity: 0, x: isRTL ? -30 : 30 }}
                        animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: isRTL ? -30 : 30 }}
                        transition={{ delay: 0.7, duration: 1.2, ease: "easeOut" }}
                        className="relative overflow-hidden"
                    >
                        <motion.img
                            src={getAsset(config.assets.aboutBg || 'about-bg.webp')}
                            alt="Who We Are"
                            className="w-full h-96 object-cover rounded-lg overflow-hidden"
                            whileHover={{ scale: 1.02 }}
                            transition={{ duration: 0.6 }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-lg"></div>
                    </motion.div>
                </div>

                {/* Core Values Section - New Design */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                    transition={{ delay: 1.3, duration: 1.2, ease: "easeOut" }}
                    className="mb-20"
                >
                    <motion.div
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 30 }}
                        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                        transition={{ delay: 0.5, duration: 1.2, ease: "easeOut" }}
                    >
                        <h3 className="text-4xl font-light text-gray-900 mb-6">
                            {t("about.company.values.title")}
                        </h3>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            {t("about.company.values.subtitle") || "Our fundamental principles that guide every decision and action"}
                        </p>
                    </motion.div>

                    {/* Core Values Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto auto-rows-fr">
                        {(() => {
                            // Define values with Lucide icons and localized content
                            const values = config.name === "EMCOR" ? [
                                {
                                    key: "innovation",
                                    icon: Lightbulb,
                                    title: t("about.company.values.innovation"),
                                    description: t("about.company.values.innovation_description") || "Pioneering cutting-edge solutions and technological advancements",
                                    color: "from-blue-500/20 to-cyan-500/20",
                                    iconColor: "text-blue-600"
                                },
                                {
                                    key: "safety",
                                    icon: Shield,
                                    title: t("about.company.values.safety"),
                                    description: t("about.company.values.safety_description") || "Ensuring the highest safety standards across all operations",
                                    color: "from-green-500/20 to-emerald-500/20",
                                    iconColor: "text-green-600"
                                },
                                {
                                    key: "sustainability",
                                    icon: Leaf,
                                    title: t("about.company.values.sustainability"),
                                    description: t("about.company.values.sustainability_description") || "Committed to environmental stewardship and sustainable practices",
                                    color: "from-emerald-500/20 to-teal-500/20",
                                    iconColor: "text-emerald-600"
                                },
                                {
                                    key: "integrity",
                                    icon: Scale,
                                    title: t("about.company.values.integrity"),
                                    description: t("about.company.values.integrity_description") || "Maintaining the highest ethical standards in all business dealings",
                                    color: "from-purple-500/20 to-indigo-500/20",
                                    iconColor: "text-purple-600"
                                },
                                {
                                    key: "partnership",
                                    icon: Handshake,
                                    title: t("about.company.values.partnership"),
                                    description: t("about.company.values.partnership_description") || "Building strong partnerships for mutual growth and success",
                                    color: "from-orange-500/20 to-amber-500/20",
                                    iconColor: "text-orange-600"
                                },
                                {
                                    key: "future",
                                    icon: Rocket,
                                    title: t("about.company.values.future"),
                                    description: t("about.company.values.future_description") || "Preparing for tomorrow's challenges with innovative solutions",
                                    color: "from-red-500/20 to-pink-500/20",
                                    iconColor: "text-red-600"
                                }
                            ] : [
                                {
                                    key: "integrity",
                                    icon: Scale,
                                    title: t("about.company.values.integrity"),
                                    description: t("about.company.values.integrity_description") || "Operating with transparency and ethical business practices",
                                    color: "from-purple-500/20 to-indigo-500/20",
                                    iconColor: "text-purple-600"
                                },
                                {
                                    key: "innovation",
                                    icon: Lightbulb,
                                    title: t("about.company.values.innovation"),
                                    description: t("about.company.values.innovation_description") || "Embracing new technologies and forward-thinking solutions",
                                    color: "from-blue-500/20 to-cyan-500/20",
                                    iconColor: "text-blue-600"
                                },
                                {
                                    key: "partnership",
                                    icon: Handshake,
                                    title: t("about.company.values.partnership"),
                                    description: t("about.company.values.partnership_description") || "Building lasting relationships with global partners",
                                    color: "from-orange-500/20 to-amber-500/20",
                                    iconColor: "text-orange-600"
                                },
                                {
                                    key: "sustainability",
                                    icon: Leaf,
                                    title: t("about.company.values.sustainability"),
                                    description: t("about.company.values.sustainability_description") || "Committed to responsible energy and environmental stewardship",
                                    color: "from-emerald-500/20 to-teal-500/20",
                                    iconColor: "text-emerald-600"
                                },
                                {
                                    key: "excellence",
                                    icon: Shield,
                                    title: t("about.company.values.excellence"),
                                    description: t("about.company.values.excellence_description") || "Maintaining the highest standards in all our operations",
                                    color: "from-indigo-500/20 to-purple-500/20",
                                    iconColor: "text-indigo-600"
                                }
                            ];

                            return values.map((value, index) => {
                                const IconComponent = value.icon;

                                return (
                                    <motion.div
                                        key={value.key}
                                        className="group relative h-full flex flex-col"
                                        initial={{ opacity: 0, y: 30 }}
                                        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                                        transition={{ delay: 1.8 + index * 0.1, duration: 0.8, ease: "easeOut" }}
                                        whileHover={{ y: -8 }}
                                    >
                                        {/* Card */}
                                        <div className="relative bg-white rounded-2xl p-8 transition-all duration-500 border border-gray-100 group-hover:border-gray-200 overflow-hidden h-full flex flex-col">
                                            {/* Background gradient */}
                                            <div className={`absolute inset-0 bg-gradient-to-br ${value.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>

                                            {/* Content */}
                                            <div className="relative z-10 flex flex-col h-full">
                                                {/* Icon */}
                                                <motion.div
                                                    className={`w-16 h-16 rounded-xl bg-gradient-to-br ${value.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 flex-shrink-0`}
                                                    whileHover={{ rotate: 5 }}
                                                >
                                                    <IconComponent className={`w-8 h-8 ${value.iconColor} group-hover:scale-110 transition-transform duration-300`} />
                                                </motion.div>

                                                {/* Title */}
                                                <h4 className="text-xl font-semibold text-gray-900 mb-4 group-hover:text-gray-800 transition-colors duration-300 flex-shrink-0">
                                                    {value.title}
                                                </h4>

                                                {/* Description */}
                                                <p className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-300 flex-grow">
                                                    {value.description}
                                                </p>

                                                {/* Hover effect line */}
                                                <div className="absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-500"></div>
                                            </div>

                                            {/* Floating elements */}
                                            <div className="absolute top-4 right-4 w-2 h-2 bg-primary/20 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:scale-150"></div>
                                            <div className="absolute bottom-4 left-4 w-1 h-1 bg-secondary/30 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-700 group-hover:scale-200"></div>
                                        </div>
                                    </motion.div>
                                );
                            });
                        })()}
                    </div>
                </motion.div>

                {/* ESG Focus Section */}
                <div className="relative w-full max-w-4xl mx-auto rounded-2xl overflow-hidden my-20">
                    <div className="flex flex-col md:flex-row w-full h-auto md:h-96 select-none">
                        {/* Environmental Responsibility */}
                        <div className="group relative w-1/3 h-full bg-cover bg-center transition-all duration-500 cursor-pointer hover:w-1/2 focus:w-1/2"
                            style={{ backgroundImage: `url('${getAsset(config.assets.esgEnvironmental || 'environmental.webp')}')` }}
                            tabIndex={0}>
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent group-hover:from-primary/80 transition-all duration-500" />
                            <div className="absolute inset-0 flex flex-col justify-end p-4 md:p-6 text-white opacity-80 group-hover:opacity-100 transition-all duration-500 bg-black/40 rounded-top-right-xl">
                                <h4 className="text-xl md:text-3xl font-light text-white mb-3 md:mb-6 drop-shadow-md">
                                    {t("about.company.esg.environmental.title")}
                                </h4>
                                <p className="text-xs md:text-sm">
                                    {t("about.company.esg.environmental.description")}
                                </p>
                            </div>
                        </div>
                        {/* Social Impact */}
                        <div className="group relative w-1/3 h-full bg-cover bg-center transition-all duration-500 cursor-pointer hover:w-1/2 focus:w-1/2"
                            style={{ backgroundImage: `url('${getAsset(config.assets.esgSocial || 'social.webp')}')` }}
                            tabIndex={0}>
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent group-hover:from-secondary/80 transition-all duration-500" />
                            <div className="absolute inset-0 flex flex-col justify-end p-4 md:p-6 text-white opacity-80 group-hover:opacity-100 transition-all duration-500 bg-black/40">
                                <h4 className="text-xl md:text-3xl font-light text-white mb-3 md:mb-6 drop-shadow-md">
                                    {t("about.company.esg.social.title")}
                                </h4>
                                <p className="text-xs md:text-sm">
                                    {t("about.company.esg.social.description")}
                                </p>
                            </div>
                        </div>
                        {/* Governance Excellence */}
                        <div className="group relative w-1/3 h-full bg-cover bg-center transition-all duration-500 cursor-pointer hover:w-1/2 focus:w-1/2"
                            style={{ backgroundImage: `url('${getAsset(config.assets.esgGovernance || 'governance.webp')}')` }}
                            tabIndex={0}>
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent group-hover:from-secondary/80 transition-all duration-500" />
                            <div className="absolute inset-0 flex flex-col justify-end p-4 md:p-6 text-white opacity-80 group-hover:opacity-100 transition-all duration-500 bg-black/40 rounded-top-left-xl">
                                <h4 className="text-xl md:text-3xl font-light text-white mb-3 md:mb-6 drop-shadow-md">
                                    {t("about.company.esg.governance.title")}
                                </h4>
                                <p className="text-xs md:text-sm">
                                    {t("about.company.esg.governance.description")}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
